/**
 * Customer Reviews — Google Apps Script backend.
 * Submissions: POST JSON to Apps Script Web App, which uploads images to GCS and appends row to Sheet.
 * Display: GET JSON from same Apps Script Web App, filtered by product handle + approval status.
 */
(function () {
  'use strict';

  const MAX_PHOTOS = 4;
  const MAX_PHOTO_BYTES = 4 * 1024 * 1024; // 4MB source cap (base64 inflates ~33%)

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.from(root.querySelectorAll(sel)); }

  function escapeHtml(s) {
    if (s == null) return '';
    const d = document.createElement('div');
    d.textContent = String(s);
    return d.innerHTML;
  }

  // Strict allow-list for URLs we'll render in <img src> or as a lightbox
  // target. Stops any javascript:/data: shenanigans even if a sheet row is
  // tampered with by a bad-actor moderator.
  function isSafeHttpUrl(s) {
    if (typeof s !== 'string') return false;
    try {
      const u = new URL(s, window.location.origin);
      return u.protocol === 'https:' || u.protocol === 'http:';
    } catch (_) {
      return false;
    }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  function init(root) {
    const endpoint = root.dataset.endpoint;
    const submitDelayMs = parseInt(root.dataset.submitDelayMs, 10) || 3000;
    const productHandle = root.dataset.productHandle || '';
    const productTitle = root.dataset.productTitle || '';
    let availableVariants = [];
    try {
      availableVariants = JSON.parse(root.dataset.variants || '[]');
      if (!Array.isArray(availableVariants)) availableVariants = [];
    } catch (_) { availableVariants = []; }

    const mountedAt = Date.now();

    const modal = $(root, '[data-cr-modal]');
    const openBtn = $(root, '[data-cr-open]');
    const closeBtn = $(root, '[data-cr-close]');
    const form = $(root, '[data-cr-form]');
    const errorBox = $(root, '[data-cr-error]');
    const successBox = $(root, '[data-cr-success]');
    const submitBtn = $(root, '[data-cr-submit]');
    const submitText = submitBtn ? submitBtn.querySelector('.cr-btn-text') : null;
    const submitLoader = submitBtn ? submitBtn.querySelector('.cr-loader') : null;
    const fileInput = $(root, '[data-cr-files]');
    const fileLabel = $(root, '[data-cr-file-label]');
    const previewWrap = $(root, '[data-cr-preview]');
    const listEl = $(root, '[data-cr-list]');
    const countEl = $(root, '[data-cr-count]');
    const lightbox = $(root, '[data-cr-lightbox]');
    const lightboxImg = $(root, '[data-cr-lightbox-img]');
    const lightboxClose = $(root, '[data-cr-lightbox-close]');
    const variantSelect = $(root, '[data-cr-variant-select]');
    const filterEl = $(root, '[data-cr-filter]');
    const filterChipsEl = $(root, '[data-cr-filter-chips]');
    const avgEl = $(root, '[data-cr-avg]');
    const summaryStarsEl = $(root, '[data-cr-stars]');

    let allReviews = [];
    let currentFilter = '';

    // --- Modal control ---
    let lastFocused = null;
    const openModal = () => {
      if (!modal) return;
      lastFocused = document.activeElement;
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-visible'));
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    };
    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('is-visible');
      setTimeout(() => { modal.hidden = true; }, 300);
      document.body.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    };

    if (openBtn) openBtn.addEventListener('click', () => {
      // Pre-select the variant the user has currently chosen on the product page,
      // if we can read it. This is purely a convenience — they can clear it.
      try {
        if (variantSelect && !variantSelect.value) {
          const variantIdInput = document.getElementById('variant-id');
          const selectedRadio = document.querySelector('.variant-radio:checked');
          const selectedTitle = (selectedRadio && (selectedRadio.dataset.variantTitle || selectedRadio.value))
            || (variantIdInput && variantIdInput.dataset && variantIdInput.dataset.variantTitle)
            || '';
          if (selectedTitle) {
            const opt = Array.from(variantSelect.options).find(o => o.value === selectedTitle);
            if (opt) variantSelect.value = selectedTitle;
          }
        }
      } catch (_) { /* non-fatal */ }
      openModal();
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (modal) {
      modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    // --- File input preview ---
    if (fileInput) {
      fileInput.addEventListener('change', () => {
        previewWrap.innerHTML = '';
        const files = Array.from(fileInput.files || []).slice(0, MAX_PHOTOS);
        if (files.length === 0) {
          if (fileLabel) fileLabel.textContent = 'Click to upload images';
          return;
        }
        if (fileLabel) fileLabel.textContent = files.length + ' image(s) selected';
        files.forEach(f => {
          const img = document.createElement('img');
          img.className = 'cr-preview-thumb';
          img.alt = '';
          fileToDataUrl(f).then(url => { img.src = url; }).catch(() => {});
          previewWrap.appendChild(img);
        });
      });
    }

    // --- Submission ---
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!endpoint) {
          showError('Review submissions are not configured. Please try again later.');
          return;
        }

        hide(errorBox);
        hide(successBox);

        // Honeypot check
        const hp = form.querySelector('.cr-honeypot');
        if (hp && hp.value) {
          showError('Submission blocked.');
          return;
        }

        // Render-to-submit delay
        if (Date.now() - mountedAt < submitDelayMs) {
          showError('Please take a moment longer to review your submission.');
          return;
        }

        const data = new FormData(form);
        const rating = parseInt(data.get('rating'), 10);
        const name = (data.get('name') || '').toString().trim();
        const email = (data.get('email') || '').toString().trim();
        const title = (data.get('title') || '').toString().trim();
        const comment = (data.get('comment') || '').toString().trim();
        const reviewerLocation = (data.get('location') || '').toString().trim();
        const reviewerVariant = (data.get('variant') || '').toString().trim();

        if (!rating || rating < 1 || rating > 5) { showError('Please choose a rating.'); return; }
        if (!name || !title || !comment) { showError('Please fill in all required fields.'); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showError('Please enter a valid email.'); return; }

        // Photos — read files to base64 (capped by count + size)
        const files = Array.from((fileInput && fileInput.files) || []).slice(0, MAX_PHOTOS);
        let images = [];
        try {
          images = await Promise.all(files.map(async (f) => {
            if (f.size > MAX_PHOTO_BYTES) {
              throw new Error('"' + f.name + '" is too large. Max 4MB per image.');
            }
            const dataUrl = await fileToDataUrl(f);
            const commaIdx = dataUrl.indexOf(',');
            return {
              filename: f.name,
              mime: f.type || 'image/jpeg',
              base64: commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl
            };
          }));
        } catch (err) {
          showError(err.message || 'Could not read one of the images.');
          return;
        }

        setSubmitting(true);

        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            // Use text/plain to avoid preflight — Apps Script accepts JSON in the body anyway.
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify({
              action: 'submit',
              product_handle: productHandle,
              product_title: productTitle,
              rating: rating,
              title: title,
              comment: comment,
              name: name,
              email: email,
              location: reviewerLocation,
              variant: reviewerVariant,
              available_variants: availableVariants,
              images: images,
              page_url: window.location ? window.location.href : '',
              user_agent: navigator.userAgent
            })
          });
          const json = await res.json();
          if (!res.ok || json.error) {
            throw new Error(json.error || ('Request failed (' + res.status + ')'));
          }
          show(successBox);
          form.reset();
          if (previewWrap) previewWrap.innerHTML = '';
          if (fileLabel) fileLabel.textContent = 'Click to upload images';
          setTimeout(closeModal, 2500);
        } catch (err) {
          console.error('Review submission failed:', err);
          showError('We couldn\'t submit your review. Please try again in a moment.');
        } finally {
          setSubmitting(false);
        }
      });
    }

    // --- Reviews load & render ---
    async function loadReviews() {
      if (!endpoint) {
        listEl.innerHTML = '<div class="cr-empty">Reviews are not configured yet.</div>';
        return;
      }
      try {
        const url = endpoint + (endpoint.indexOf('?') >= 0 ? '&' : '?') +
                    'action=list&product_handle=' + encodeURIComponent(productHandle);
        const res = await fetch(url, { method: 'GET' });
        const json = await res.json();
        if (!Array.isArray(json.reviews)) throw new Error('Malformed response');
        allReviews = json.reviews;
        updateFilterChipCounts();
        renderReviews(allReviews);
      } catch (err) {
        console.error('Failed to load reviews:', err);
        listEl.innerHTML = '<div class="cr-empty">Reviews are temporarily unavailable.</div>';
      }
    }

    // Recompute the header summary (count, average number, star fill) from
    // the live approved reviews — overrides the metafield defaults rendered
    // by Liquid so the page is always in sync with the Sheet.
    function updateSummary(reviews) {
      const total = reviews.length;
      if (countEl) countEl.textContent = String(total);

      if (!total) {
        // No approved reviews — don't claim a 5.0 average. Show 0.0 + empty stars.
        if (avgEl) avgEl.textContent = '0.0';
        if (summaryStarsEl) {
          summaryStarsEl.textContent = '☆☆☆☆☆';
          summaryStarsEl.setAttribute('aria-label', 'No ratings yet');
        }
        syncTitleBadge(0, 0);
        return;
      }

      const sum = reviews.reduce((acc, r) => {
        const v = parseInt(r.rating, 10);
        return acc + (Number.isFinite(v) ? Math.max(1, Math.min(5, v)) : 0);
      }, 0);
      const avg = sum / total;
      const avgRounded = Math.round(avg * 10) / 10; // one decimal

      if (avgEl) avgEl.textContent = avgRounded.toFixed(1);

      if (summaryStarsEl) {
        const filled = Math.round(avg); // visual approximation
        summaryStarsEl.textContent =
          '★'.repeat(Math.max(0, Math.min(5, filled))) +
          '☆'.repeat(Math.max(0, 5 - filled));
        summaryStarsEl.setAttribute('aria-label',
          'Rated ' + avgRounded.toFixed(1) + ' out of 5 from ' + total + ' review' + (total === 1 ? '' : 's'));
      }

      syncTitleBadge(total, avg);
    }

    // Sync the Trustpilot-style rating badge under the product title
    // (rendered by main-product.liquid from metafields, which may be stale
    // or absent — the live approved reviews are the source of truth).
    function syncTitleBadge(total, avg) {
      const badge = document.querySelector('[data-product-rating-badge]');
      if (!badge) return;
      if (!total) { badge.hidden = true; return; }
      badge.hidden = false;

      const rounded = (Math.round(avg * 10) / 10).toFixed(1);
      $$(badge, '.tp-star').forEach((box, i) => {
        const fill = Math.max(0, Math.min(100, (avg - i) * 100));
        box.style.setProperty('--fill', fill + '%');
        box.classList.toggle('tp-star--empty', fill < 50);
      });

      const scoreEl = badge.querySelector('[data-pr-score]');
      if (scoreEl) scoreEl.textContent = rounded;
      const linkTextEl = badge.querySelector('[data-pr-count]');
      if (linkTextEl) {
        linkTextEl.textContent = 'Click to read ' + total + ' review' + (total === 1 ? '' : 's');
      }
      badge.setAttribute('aria-label',
        'Rated ' + rounded + ' out of 5 stars — click to read customer reviews');
    }

    function renderReviews(reviews) {
      // Apply current variant filter (empty = show all)
      const filtered = currentFilter
        ? reviews.filter(r => (r.variant || '').trim() === currentFilter)
        : reviews;

      updateSummary(reviews);

      if (!filtered.length) {
        const msg = currentFilter
          ? 'No reviews yet for "' + escapeHtml(currentFilter) + '". Try another variant or view all.'
          : 'No reviews yet. Be the first to write a review!';
        listEl.innerHTML = '<div class="cr-empty">' + msg + '</div>';
        return;
      }

      const html = filtered.map(r => {
        const rating = Math.max(1, Math.min(5, parseInt(r.rating, 10) || 5));
        const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating);
        const initial = (r.name || 'A').trim().charAt(0).toUpperCase();
        const loc = r.location ? ', ' + escapeHtml(r.location) : '';
        const variant = (r.variant || '').trim();
        const variantHtml = variant
          ? '<span class="cr-card-variant" title="Variant reviewed">' + escapeHtml(variant) + '</span>'
          : '';
        const images = (Array.isArray(r.images) ? r.images : []).filter(isSafeHttpUrl);
        const photoHtml = images.length
          ? '<div class="cr-card-photos">' + images.map(src =>
              '<button type="button" class="cr-card-photo" data-cr-photo data-full-src="' +
              escapeHtml(src) + '">' +
              '<img src="' + escapeHtml(src) + '" alt="" loading="lazy" decoding="async" width="200" height="200">' +
              '</button>'
            ).join('') + '</div>'
          : '';
        return (
          '<article class="cr-card" data-variant="' + escapeHtml(variant) + '">' +
            '<div class="cr-card-header">' +
              '<div class="cr-reviewer">' +
                '<div class="cr-avatar" aria-hidden="true">' + escapeHtml(initial) + '</div>' +
                '<div class="cr-reviewer-meta">' +
                  '<span class="cr-reviewer-name">' + escapeHtml(r.name || 'Anonymous') + loc + '</span>' +
                  '<span class="cr-verified">Verified Buyer</span>' +
                '</div>' +
              '</div>' +
              '<div class="cr-card-stars" aria-label="' + rating + ' out of 5 stars">' + stars + '</div>' +
            '</div>' +
            (r.title ? '<h3 class="cr-card-title">' + escapeHtml(r.title) + '</h3>' : '') +
            '<p class="cr-card-body">' + escapeHtml(r.comment || '') + '</p>' +
            (variantHtml ? '<div class="cr-card-meta">' + variantHtml + '</div>' : '') +
            photoHtml +
          '</article>'
        );
      }).join('');
      listEl.innerHTML = html;
    }

    // --- Variant filter chips ---
    function updateFilterChipCounts() {
      if (!filterChipsEl) return;
      const counts = {};
      allReviews.forEach(r => {
        const v = (r.variant || '').trim();
        counts[v] = (counts[v] || 0) + 1;
      });
      const chips = filterChipsEl.querySelectorAll('[data-cr-chip]');
      let anyVariantHasReviews = false;
      chips.forEach(chip => {
        const key = chip.getAttribute('data-cr-chip') || '';
        const count = key === '' ? allReviews.length : (counts[key] || 0);
        // Stash count in a data attr for optional CSS rendering
        chip.dataset.count = String(count);
        // Hide variant chips that have zero reviews to avoid empty filters
        if (key !== '' && count === 0) {
          chip.hidden = true;
        } else {
          chip.hidden = false;
          if (key !== '') anyVariantHasReviews = true;
        }
      });
      // Only show the filter bar if at least one variant has reviews
      if (filterEl) filterEl.hidden = !anyVariantHasReviews;
    }

    if (filterChipsEl) {
      filterChipsEl.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-cr-chip]');
        if (!chip) return;
        currentFilter = chip.getAttribute('data-cr-chip') || '';
        filterChipsEl.querySelectorAll('[data-cr-chip]').forEach(c => {
          const isActive = c === chip;
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        renderReviews(allReviews);
      });
    }

    // --- Lightbox (delegated) ---
    if (lightbox && lightboxImg) {
      listEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cr-photo]');
        if (!btn) return;
        const src = btn.dataset.fullSrc || '';
        if (!isSafeHttpUrl(src)) return;
        lightboxImg.src = src;
        lightboxImg.alt = '';
        lightbox.hidden = false;
        document.body.style.overflow = 'hidden';
      });
      const closeLb = () => {
        lightbox.hidden = true;
        document.body.style.overflow = '';
      };
      if (lightboxClose) lightboxClose.addEventListener('click', closeLb);
      lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLb(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !lightbox.hidden) closeLb();
      });
    }

    // --- Helpers ---
    function show(el) { if (el) el.hidden = false; }
    function hide(el) { if (el) el.hidden = true; }
    function showError(msg) { if (errorBox) { errorBox.textContent = msg; show(errorBox); } }
    function setSubmitting(is) {
      if (!submitBtn) return;
      submitBtn.disabled = is;
      if (submitText) submitText.hidden = is;
      if (submitLoader) submitLoader.hidden = !is;
    }

    // Kick off
    loadReviews();
  }

  function boot() {
    document.querySelectorAll('[data-cr]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
